import { z } from 'zod';

/**
 * Shared phone / mobile number validation.
 *
 * Used across the app (Company Branding contact number, User phone & mobile
 * fields) so the rules stay consistent. All phone fields are optional, so an
 * empty value is always allowed.
 */

// Accepts an optional leading "+", parentheses and separators (space, dash, dot)
// in the usual phone-number positions.
export const PHONE_REGEX =
  /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;

// Minimum number of actual digits required (ignoring formatting characters).
export const MIN_PHONE_DIGITS = 7;

export const PHONE_FORMAT_MESSAGE = 'Please enter a valid phone number';
export const PHONE_MIN_DIGITS_MESSAGE = `Phone number must have at least ${MIN_PHONE_DIGITS} digits`;

// The same rules back both the Phone and Mobile fields, so the field label is
// parameterised - otherwise the Mobile field reports "Phone number must have
// at least 7 digits", which reads as though the wrong field is at fault.
const formatMessage = (label: string) => `Please enter a valid ${label.toLowerCase()}`;
const minDigitsMessage = (label: string) =>
  `${label} must have at least ${MIN_PHONE_DIGITS} digits`;

/** Returns true if the value is a structurally valid phone number. */
export function isValidPhoneNumber(value: string): boolean {
  return PHONE_REGEX.test(value);
}

/** Returns the count of digit characters in the value. */
export function countPhoneDigits(value: string): number {
  return value.replace(/\D/g, '').length;
}

/**
 * react-hook-form `rules` object for an optional phone field.
 * Spread into a FormField: rules={phoneRules}
 */
export const phoneRules = {
  pattern: {
    value: PHONE_REGEX,
    message: PHONE_FORMAT_MESSAGE,
  },
  validate: (v?: string) =>
    !v || countPhoneDigits(v) >= MIN_PHONE_DIGITS || PHONE_MIN_DIGITS_MESSAGE,
} as const;

/**
 * Zod schema for an optional phone field. Empty/undefined passes; non-empty
 * values must match the format and meet the minimum digit count.
 */
export const makeOptionalPhoneSchema = (label = 'Phone number') =>
  z
    .string()
    .optional()
    .refine((v) => !v || isValidPhoneNumber(v), {
      message: formatMessage(label),
    })
    .refine((v) => !v || countPhoneDigits(v) >= MIN_PHONE_DIGITS, {
      message: minDigitsMessage(label),
    });

export const optionalPhoneSchema = makeOptionalPhoneSchema();
export const optionalMobileSchema = makeOptionalPhoneSchema('Mobile number');
